import React from 'react';
import {
  Alert, Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { fmtMoney } from '../../../Documents/format';
import { BUCKET_COLORS, CHART, actionOf, bucketOf } from '../../constants';
import { daysLeftLabel, fmtDay, fmtInt, fmtPct, fmtQty, fmtStamp, monthLabel, share, signed } from '../../format';
import { journalApi } from '../../journalApi';
import { useLoad } from '../../hooks/useLoad';
import { JournalFilters, OptionField, OptionPick, ProductResponse } from '../../types';
import { OptionFilter } from '../FilterControls';
import { BORDER, ChartCard, ChartSkeleton, NoData, ShareBar, Stat, StatStrip, SubText, TABLE_SX } from './chartKit';
import MovementChart from './MovementChart';

interface Props {
  filters: JournalFilters;
  paramsKey: string;
  active: boolean;
  reloadToken: number;
  /** Товар вкладки: фильтр «Товар» панели либо выбранный здесь / в топе товаров */
  pick: OptionPick | null;
  /** Товар задан фильтром панели - выбрать другой здесь нельзя, пока фильтр не снят */
  fromFilters: boolean;
  onLocalPick: (pick: OptionPick | null) => void;
  onPick: (field: OptionField, pick: OptionPick | null) => void;
  onPatch: (patch: Partial<JournalFilters>) => void;
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500 }} noWrap title={typeof value === 'string' ? value : undefined}>{value || '-'}</Typography>
  </Box>
);

const ProductBody: React.FC<{ data: ProductResponse; filters: JournalFilters; onPatch: Props['onPatch'] }> = ({ data, filters, onPatch }) => {
  const p = data.product;
  const selected = filters.pharmacies;
  const stats: Stat[] = [
    { label: 'Сумма в журнале', value: fmtMoney(p.sum), hint: `${fmtInt(p.count)} зап. · ${fmtQty(p.qty)} уп.` },
    { label: 'Аптек', value: fmtInt(p.pharmacies), hint: `серий: ${fmtInt(p.serials)}` },
    { label: 'Просрочено', value: fmtMoney(p.expiredSum), color: p.expiredSum ? CHART.expired : undefined, hint: `${fmtPct(share(p.expiredSum, p.sum))} · ${fmtInt(p.expiredCount)} зап.` },
    { label: 'До 30 дней', value: fmtMoney(p.d30Sum), color: p.d30Sum ? CHART.d30 : undefined, hint: `${fmtInt(p.d30Count)} зап.` },
    { label: 'До 90 дней', value: fmtMoney(p.d90Sum), hint: `${fmtPct(share(p.d90Sum, p.sum))} · включая до 30 дней` },
    { label: 'Ближайший срок', value: fmtDay(p.nearestExpiry), hint: 'из ещё не истёкших' },
    {
      label: 'Цена, мин / ср / макс', value: fmtMoney(p.avgPrice), hint: `${fmtMoney(p.minPrice)} – ${fmtMoney(p.maxPrice)}`,
      tooltip: 'Средняя - сумма, делённая на количество упаковок; разброс - по записям аптек'
    },
    { label: 'Последняя запись', value: fmtStamp(p.lastRecord) }
  ];
  const maxPharmacy = data.byPharmacy.length ? data.byPharmacy[0].sum : 0;
  const pickPharmacy = (guid: string) => onPatch({ pharmacies: selected.length === 1 && selected[0] === guid ? [] : [guid] });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <ChartCard wide title={p.name} subtitle={`Реквизиты - из последней записи аптеки. ${data.month ? `Период: ${monthLabel(data.month).toLowerCase()}.` : 'Все периоды.'}`}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5, mb: 1.5, pb: 1.5, borderBottom: `1px solid ${BORDER}` }}>
          <Field label="Код" value={p.code} />
          <Field label="Штрихкод" value={p.barcode} />
          <Field label="Производитель" value={p.producer} />
          <Field label="Категория" value={p.category} />
          <Field label="Менеджер" value={p.manager} />
          <Field label="Ценовая группа" value={p.priceGroup} />
        </Box>
        {!p.count ? <NoData height={100} text="По выбранным фильтрам записей с этим товаром нет" /> : <StatStrip items={stats} />}
      </ChartCard>

      <ChartCard wide title="Движение товара по истории" subtitle="Внесено, правки и удаления по дате действия, линия - остаток журнала по товару">
        <MovementChart data={data.movement} height={260} />
      </ChartCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' }, gap: 1.5 }}>
        <ChartCard title="По аптекам" subtitle={`${data.byPharmacy.length >= 100 ? 'Первые 100 по сумме' : `${fmtInt(data.byPharmacy.length)} аптек`}. Клик - фильтр по аптеке.`}>
          {!data.byPharmacy.length ? <NoData height={120} /> : (
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell>Аптека</TableCell>
                    <TableCell align="right">Сумма</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Доля</TableCell>
                    <TableCell align="right">Записей</TableCell>
                    <TableCell align="right">Уп.</TableCell>
                    <TableCell align="right">Просрочено</TableCell>
                    <TableCell>Ближайший срок</TableCell>
                    <TableCell>Последняя запись</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.byPharmacy.map(r => (
                    <TableRow key={r.guid} hover selected={selected.indexOf(r.guid) !== -1} onClick={() => pickPharmacy(r.guid)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={r.name}>{r.name}</Typography>
                        <SubText>{r.num}{r.region ? ` · ${r.region}` : ''}</SubText>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(r.sum)}</TableCell>
                      <TableCell><ShareBar value={r.sum} max={maxPharmacy} label={fmtPct(share(r.sum, p.sum))} /></TableCell>
                      <TableCell align="right">{fmtInt(r.count)}</TableCell>
                      <TableCell align="right">{fmtQty(r.qty)}</TableCell>
                      <TableCell align="right" sx={{ color: r.expiredSum ? `${CHART.expired} !important` : undefined }}>{r.expiredSum ? fmtMoney(r.expiredSum) : '-'}</TableCell>
                      <TableCell>{fmtDay(r.nearestExpiry)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStamp(r.lastRecord)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ChartCard>

        <ChartCard title="Серии и сроки" subtitle={`${data.bySeries.length >= 100 ? 'Первые 100' : fmtInt(data.bySeries.length)} по сроку годности, от ближайшего`}>
          {!data.bySeries.length ? <NoData height={120} /> : (
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell>Серия</TableCell>
                    <TableCell>Срок годности</TableCell>
                    <TableCell align="right">Сумма</TableCell>
                    <TableCell align="right">Уп.</TableCell>
                    <TableCell align="right">Аптек</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.bySeries.map((s, i) => {
                    const b = BUCKET_COLORS[bucketOf(s.daysLeft)];
                    return (
                      <TableRow key={`${s.serial}-${s.expiryDate}-${i}`}>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Typography sx={{ fontSize: '0.78rem' }} noWrap title={s.serial}>{s.serial || 'без серии'}</Typography>
                          <SubText>{fmtInt(s.count)} зап.</SubText>
                        </TableCell>
                        <TableCell>
                          <Box component="span" sx={{ display: 'inline-block', px: 0.75, borderRadius: 1, bgcolor: b.bg, color: b.color, fontWeight: 600, fontSize: '0.76rem' }}>
                            {fmtDay(s.expiryDate)}
                          </Box>
                          <SubText color={b.color}>{daysLeftLabel(s.daysLeft)}</SubText>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(s.sum)}</TableCell>
                        <TableCell align="right">{fmtQty(s.qty)}</TableCell>
                        <TableCell align="right">{fmtInt(s.pharmacies)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ChartCard>
      </Box>

      <ChartCard wide title="Последние изменения" subtitle={`${data.movements.length >= 200 ? 'Последние 200' : fmtInt(data.movements.length)} действий по товару, от новых к старым`}>
        {!data.movements.length ? <NoData height={100} text="Изменений в истории нет" /> : (
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 460 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell>Когда</TableCell>
                  <TableCell>Действие</TableCell>
                  <TableCell>Аптека</TableCell>
                  <TableCell>Серия / срок</TableCell>
                  <TableCell align="right">Кол-во, уп.</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                  <TableCell>Кто</TableCell>
                  <TableCell>Комментарий</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.movements.map(m => {
                  const a = actionOf(m.action);
                  return (
                    <TableRow key={m.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStamp(m.date)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={a.label} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: a.color, bgcolor: a.bg }} />
                      </TableCell>
                      <TableCell>{m.pharmacyNum || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography sx={{ fontSize: '0.76rem' }} noWrap>{m.serialNumber || 'без серии'}</Typography>
                        <SubText>{fmtDay(m.expiryDate)}</SubText>
                      </TableCell>
                      <TableCell align="right">
                        {fmtQty(m.quantity)}
                        {m.quantityDelta ? <SubText color={m.quantityDelta > 0 ? '#15803d' : '#b91c1c'}>{signed(m.quantityDelta, fmtQty)}</SubText> : null}
                      </TableCell>
                      <TableCell align="right">
                        {fmtMoney(m.totalSum, 2)}
                        {m.totalSumDelta ? <SubText color={m.totalSumDelta > 0 ? '#15803d' : '#b91c1c'}>{signed(m.totalSumDelta, x => fmtMoney(x, 2))}</SubText> : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140 }}><Typography sx={{ fontSize: '0.76rem' }} noWrap>{m.actionBy || '-'}</Typography></TableCell>
                      <TableCell sx={{ maxWidth: 200 }}><Typography sx={{ fontSize: '0.74rem', color: '#475569' }} noWrap title={m.comment}>{m.comment || ''}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ChartCard>
    </Box>
  );
};

const ProductView: React.FC<Props> = ({ filters, paramsKey, active, reloadToken, pick, fromFilters, onLocalPick, onPick, onPatch }) => {
  const code = pick ? pick.value : '';
  const state = useLoad(
    active && code ? (silent: boolean, signal: AbortSignal) => journalApi.product(code, filters, silent, signal) : null,
    `${code}|${paramsKey}`, reloadToken, 'Не удалось построить карточку товара'
  );
  const data = state.data && state.data.product.code === code ? state.data : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {fromFilters ? (
          <Alert severity="info" icon={false} sx={{ py: 0, fontSize: '0.78rem', flexGrow: 1 }} action={<Button color="inherit" size="small" onClick={() => onPick('product', null)}>Снять фильтр</Button>}>
            Товар задан фильтром «Товар» на панели: {pick?.label}. Чтобы открыть другой, снимите фильтр.
          </Alert>
        ) : (
          <>
            <OptionFilter
              field="product" label="Товар" hint="Название, код или ШК" value={pick} filters={filters}
              onChange={onLocalPick} minWidth={320}
            />
            {pick && (
              <Button size="small" variant="outlined" onClick={() => onPick('product', pick)} sx={{ textTransform: 'none' }}>
                Отфильтровать журнал по товару
              </Button>
            )}
          </>
        )}
      </Box>

      {!code && (
        <ChartCard wide title="Карточка товара">
          <NoData height={160} text="Выберите товар выше или кликните по строке в топе товаров на вкладке «Структура»" />
        </ChartCard>
      )}
      {code && !data && (
        <ChartCard wide title={pick?.label || 'Карточка товара'} loading={state.loading} error={state.error?.text} onRetry={state.retry}>
          {!state.error && <ChartSkeleton height={360} />}
        </ChartCard>
      )}
      {code && data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: state.loading ? 0.65 : 1, transition: 'opacity 0.15s' }}>
          {state.error && (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Повторить</Button>}>
              {state.error.text}. На экране - данные прежнего запроса.
            </Alert>
          )}
          <ProductBody data={data} filters={filters} onPatch={onPatch} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ProductView);
